/**
 * Outbox de play logs (proof-of-play) persistido em IndexedDB.
 *
 * Regras (F-10):
 *  - Todo evento é gravado no IndexedDB já no enqueue — reload/queda de energia não perde nada
 *    que já tocou. Se o IDB falhar, cai para um buffer em memória limitado (último recurso).
 *  - Flush a cada 30s, quando >= 50 eventos novos, no `start()` (backlog pós-reload) e no evento
 *    `online`. Nenhuma tentativa com `navigator.onLine === false`.
 *  - Envio em lotes de 50. Item aceito → removido da fila.
 *  - Falha transitória (rede, timeout, 5xx, 429, 401/403/404, item `rejected` pela API) →
 *    backoff exponencial por item (`nextAttemptAt`, teto 15 min) e retry sem limite.
 *  - 4xx de validação (400/413/422) → o lote é bisseccionado até isolar o(s) item(ns) inválido(s);
 *    só o item isolado conta rejeições, e vira `dead` após 5.
 *  - Dead-letter limitado a 2000 (mais antigos primeiro).
 *  - Itens reenviáveis limitados a MAX_RETRYABLE_ITEMS (L-14): acima disso os mais antigos são
 *    descartados e contados (getDroppedCount), para um player offline por semanas não esgotar o IDB.
 *  - A bisseção de um lote com 4xx de validação tem orçamento de requests (L-14).
 */

import { playLogService } from '@/services/play-log.service'
import type { PlayLogPayload, PlayLogQueueItem } from '@/types/player.types'
import { createEventId } from '@/utils/event-id'
import { playerLogger } from '@/utils/player-logger'
import { getDB } from './db'

const FLUSH_INTERVAL_MS = 30_000
const FLUSH_AT_COUNT = 50
const BATCH_SIZE = 50
const MAX_ITEMS_PER_ROUND = 500
const MAX_ROUNDS_PER_FLUSH = 20
const MAX_MEMORY_FALLBACK = 1_000
const MAX_VALIDATION_REJECTIONS = 5
const DEAD_LETTER_MAX = 2_000
const BACKOFF_BASE_MS = 30_000
const BACKOFF_MAX_MS = 15 * 60_000
const VALIDATION_STATUSES = new Set([400, 413, 422])
/**
 * Teto de itens reenviáveis no outbox (L-14, revisão 2026-10-26). ~1 KB por item → ~50 MB, cerca de
 * 8 dias de plays a cada 15 s sem conectividade. Ultrapassado, descarta os mais antigos (a prova
 * mais velha é a de menor valor comercial) e incrementa um contador persistido.
 */
export const MAX_RETRYABLE_ITEMS = 50_000
/**
 * Máximo de requests por lote durante a bisseção (L-14). Um único item inválido num lote de 50 é
 * isolado em ~13 requests; um lote todo inválido custaria 99 contra um limiter de 60/min. Esgotado o
 * orçamento, o que falta isolar fica como transitório (backoff, sem contar rejeição) e o flush para.
 */
export const MAX_REQUESTS_PER_BATCH = 16
const DROPPED_COUNTER_KEY = 'castiq-playlog-dropped'

type Outcome = 'accepted' | 'transient' | 'invalid'

interface QueueRecord {
  item: PlayLogQueueItem
  payload: PlayLogPayload
  /** true quando o IDB falhou e o item vive só no buffer em memória. */
  inMemory: boolean
}

/** Fallback quando o IDB está indisponível (quota, storage corrompido). */
const memoryQueue: PlayLogQueueItem[] = []
let pendingWrites: Promise<void> = Promise.resolve()
let enqueuedSinceFlush = 0
let flushTimer: ReturnType<typeof setTimeout> | null = null
let isRunning = false
let flushing: Promise<void> | null = null
let rerunRequested = false
let rerunForce = false
let droppedCount = readDroppedCount()
let retryableCap = MAX_RETRYABLE_ITEMS

/** Só para testes: evita gravar 50 mil itens no fake-indexeddb para exercitar o teto. */
export function setRetryableCapForTests(cap: number): void {
  retryableCap = cap
}

const handleOnline = (): void => {
  // Conectividade voltou: ignora o backoff acumulado durante a queda.
  void requestFlush(true)
}

export const playLogManager = {
  enqueue(payload: PlayLogPayload): void {
    // eventId fixado antes de persistir → reenvios são idempotentes na API.
    const withEventId: PlayLogPayload = payload.eventId ? payload : { ...payload, eventId: createEventId() }
    const now = Date.now()
    const item: PlayLogQueueItem = {
      payload: JSON.stringify(withEventId),
      createdAt: now,
      attempts: 0,
      status: 'pending',
      nextAttemptAt: now
    }

    pendingWrites = pendingWrites.then(() => persist(item))
    enqueuedSinceFlush++
    if (enqueuedSinceFlush >= FLUSH_AT_COUNT) {
      void requestFlush(false)
    }
  },

  start(): void {
    if (isRunning) return
    isRunning = true
    if (typeof window !== 'undefined') window.addEventListener('online', handleOnline)
    scheduleFlush()
    // Drena o backlog deixado por um reload/queda de energia.
    void requestFlush(false)
  },

  stop(): void {
    isRunning = false
    if (typeof window !== 'undefined') window.removeEventListener('online', handleOnline)
    if (flushTimer) {
      clearTimeout(flushTimer)
      flushTimer = null
    }
  },

  /** Envia os itens já elegíveis (respeita backoff). */
  async flush(): Promise<void> {
    await requestFlush(false)
  },

  /** Itens ainda não confirmados pela API (inclui legado `failed` e buffer em memória). */
  async getPendingCount(): Promise<number> {
    await pendingWrites
    try {
      const db = await getDB()
      const [pending, failed] = await Promise.all([
        db.countFromIndex('playLogs', 'by-status', 'pending'),
        db.countFromIndex('playLogs', 'by-status', 'failed')
      ])
      return pending + failed + memoryQueue.length
    } catch {
      return memoryQueue.length
    }
  },

  /** Itens reenviáveis descartados pelo teto do outbox desde a instalação (persistido). */
  getDroppedCount(): number {
    return droppedCount
  },

  async getDeadLetterCount(): Promise<number> {
    try {
      const db = await getDB()
      return await db.countFromIndex('playLogs', 'by-status', 'dead')
    } catch {
      return 0
    }
  }
}

function scheduleFlush(): void {
  if (!isRunning) return
  flushTimer = setTimeout(async () => {
    await requestFlush(false)
    scheduleFlush()
  }, FLUSH_INTERVAL_MS)
}

/** Single-flight: chamadas concorrentes aguardam o flush em curso e pedem uma nova rodada. */
function requestFlush(force: boolean): Promise<void> {
  if (flushing) {
    rerunRequested = true
    rerunForce = rerunForce || force
    return flushing
  }

  // Teto aplicado depois do envio: o que acabou de ser aceito já saiu da contagem.
  flushing = runFlush(force)
    .then(enforceRetryableCap)
    .finally(() => {
      flushing = null
    })
  return flushing
}

async function runFlush(force: boolean): Promise<void> {
  let nextForce = force
  for (let round = 0; round < MAX_ROUNDS_PER_FLUSH; round++) {
    rerunRequested = false
    rerunForce = false
    enqueuedSinceFlush = 0
    await pendingWrites

    let hasMore = false
    try {
      hasMore = await drainRound(nextForce)
    } catch (err) {
      playerLogger.warn('[play-log-manager] Erro no ciclo de flush', err)
    }

    if (!hasMore && !rerunRequested) return
    nextForce = rerunForce
  }
}

/** @returns true quando pode haver mais itens elegíveis (rodada cheia, sem falha transitória). */
async function drainRound(force: boolean): Promise<boolean> {
  if (isOffline()) return false

  const records = await loadDueRecords(force)
  if (records.length === 0) return false

  for (const batch of chunk(records, BATCH_SIZE)) {
    if (isOffline()) return false
    const outcomes = await send(batch, { remaining: MAX_REQUESTS_PER_BATCH })
    await applyOutcomes(batch, outcomes)
    // Rede/servidor instável: para a drenagem em vez de martelar o resto da fila.
    if (outcomes.includes('transient')) return false
  }

  return records.length >= MAX_ITEMS_PER_ROUND
}

async function loadDueRecords(force: boolean): Promise<QueueRecord[]> {
  const now = Date.now()
  const records: QueueRecord[] = []

  for (const item of memoryQueue) {
    if (records.length >= MAX_ITEMS_PER_ROUND) break
    if (!force && (item.nextAttemptAt ?? 0) > now) continue
    const payload = parsePayload(item)
    if (payload) records.push({ item, payload, inMemory: true })
  }

  const remaining = MAX_ITEMS_PER_ROUND - records.length
  if (remaining <= 0) return records

  try {
    const db = await getDB()
    // Índice ordenado por nextAttemptAt; itens dead não têm o campo e ficam fora.
    const query = force ? undefined : IDBKeyRange.upperBound(now)
    const items = await db.getAllFromIndex('playLogs', 'by-nextAttemptAt', query, remaining)
    const corrupt: PlayLogQueueItem[] = []
    for (const item of items) {
      const payload = parsePayload(item)
      if (payload) records.push({ item, payload, inMemory: false })
      else corrupt.push(item)
    }
    if (corrupt.length > 0) {
      playerLogger.warn('[play-log-manager] Itens corrompidos na fila -> dead-letter', { count: corrupt.length })
      const tx = db.transaction('playLogs', 'readwrite')
      await Promise.all([...corrupt.map((item) => tx.store.put(toDead(item))), tx.done])
      await pruneDeadLetter()
    }
  } catch (err) {
    playerLogger.warn('[play-log-manager] Falha ao ler fila do IndexedDB', err)
  }

  return records
}

/** Envia um lote; em 4xx de validação bissecciona para isolar o item inválido, dentro do orçamento. */
async function send(records: QueueRecord[], budget: { remaining: number }): Promise<Outcome[]> {
  if (budget.remaining <= 0) return records.map(() => 'transient')
  budget.remaining--
  try {
    const response = await playLogService.ingestBatch(records.map((record) => record.payload))
    const statusByIndex = new Map((response?.results ?? []).map((result) => [result.index, result.status]))
    // `rejected` por item = falha de publish na API (transitória). Índice ausente → reenviar.
    return records.map((_, index) => (statusByIndex.get(index) === 'accepted' ? 'accepted' : 'transient'))
  } catch (err) {
    if (!isValidationError(err)) return records.map(() => 'transient')
    if (records.length === 1) return ['invalid']

    const middle = Math.ceil(records.length / 2)
    // Ambas as metades sempre são enviadas: parar no primeiro transitório deixaria a ordem da fila
    // decidir se o item inválido chega a ser isolado.
    const left = await send(records.slice(0, middle), budget)
    const right = await send(records.slice(middle), budget)
    return [...left, ...right]
  }
}

async function applyOutcomes(records: QueueRecord[], outcomes: Outcome[]): Promise<void> {
  const now = Date.now()
  const toDelete: number[] = []
  const toPut: PlayLogQueueItem[] = []
  let deadLettered = 0

  for (const [index, record] of records.entries()) {
    const outcome = outcomes[index] ?? 'transient'
    const next = nextState(record.item, outcome, now)
    if (next?.status === 'dead') deadLettered++

    if (record.inMemory) {
      const position = memoryQueue.indexOf(record.item)
      if (position === -1) continue
      if (next) memoryQueue[position] = next
      else memoryQueue.splice(position, 1)
      continue
    }

    if (next) toPut.push(next)
    else if (record.item.id != null) toDelete.push(record.item.id)
  }

  if (deadLettered > 0) {
    playerLogger.warn('[play-log-manager] Itens rejeitados pela validação da API -> dead-letter', {
      count: deadLettered
    })
  }

  if (toDelete.length === 0 && toPut.length === 0) return

  try {
    const db = await getDB()
    const tx = db.transaction('playLogs', 'readwrite')
    await Promise.all([
      ...toDelete.map((id) => tx.store.delete(id)),
      ...toPut.map((item) => tx.store.put(item)),
      tx.done
    ])
    if (deadLettered > 0) await pruneDeadLetter()
  } catch (err) {
    playerLogger.warn('[play-log-manager] Falha ao atualizar fila após envio', err)
  }
}

/** @returns null quando o item deve sair da fila (aceito). */
function nextState(item: PlayLogQueueItem, outcome: Outcome, now: number): PlayLogQueueItem | null {
  if (outcome === 'accepted') return null

  const attempts = item.attempts + 1
  const validationRejections = (item.validationRejections ?? 0) + (outcome === 'invalid' ? 1 : 0)

  if (validationRejections >= MAX_VALIDATION_REJECTIONS) {
    return toDead({ ...item, attempts, validationRejections, lastAttemptAt: now })
  }

  return {
    ...item,
    status: 'pending',
    attempts,
    validationRejections,
    lastAttemptAt: now,
    nextAttemptAt: now + backoffMs(attempts)
  }
}

function toDead(item: PlayLogQueueItem): PlayLogQueueItem {
  // Sem nextAttemptAt → fora do índice de reenvio.
  const { nextAttemptAt: _omit, ...rest } = item
  return { ...rest, status: 'dead' }
}

/** Exponencial com teto de 15 min e jitter ±20% (evita rajada da frota quando a API volta). */
function backoffMs(attempts: number): number {
  const base = Math.min(BACKOFF_BASE_MS * 2 ** Math.max(0, attempts - 1), BACKOFF_MAX_MS)
  return Math.round(base * (0.8 + Math.random() * 0.4))
}

async function persist(item: PlayLogQueueItem): Promise<void> {
  try {
    const db = await getDB()
    await db.add('playLogs', item)
    return
  } catch (err) {
    playerLogger.warn('[play-log-manager] Falha ao gravar play log no IndexedDB -> tentando liberar espaço', err)
  }

  try {
    // Pode ser quota: libera dead-letter e tenta de novo.
    await pruneDeadLetter(0)
    const db = await getDB()
    await db.add('playLogs', item)
    return
  } catch {
    // segue para o fallback em memória
  }

  if (memoryQueue.length >= MAX_MEMORY_FALLBACK) {
    playerLogger.warn('[play-log-manager] Buffer em memória cheio -> descartando evento mais antigo')
    memoryQueue.shift()
  }
  memoryQueue.push(item)
}

function parsePayload(item: PlayLogQueueItem): PlayLogPayload | null {
  try {
    return JSON.parse(item.payload) as PlayLogPayload
  } catch {
    return null
  }
}

function isValidationError(err: unknown): boolean {
  const status = (err as { response?: { status?: unknown } } | null)?.response?.status
  return typeof status === 'number' && VALIDATION_STATUSES.has(status)
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false
}

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size))
  }
  return chunks
}

/** Remove os dead-letter mais antigos acima de `max`. Nunca toca em itens reenviáveis. */
async function pruneDeadLetter(max = DEAD_LETTER_MAX): Promise<void> {
  const db = await getDB()
  const deadKeys = await db.getAllKeysFromIndex('playLogs', 'by-status', 'dead')
  if (deadKeys.length <= max) return

  // Chave autoIncrement é monotônica → ordem de chave = ordem de criação.
  const toRemove = [...deadKeys].sort((a, b) => a - b).slice(0, deadKeys.length - max)
  const tx = db.transaction('playLogs', 'readwrite')
  await Promise.all([...toRemove.map((id) => tx.store.delete(id)), tx.done])
}

/**
 * Aplica MAX_RETRYABLE_ITEMS (L-14): conta barato a cada flush; só lê as chaves quando acima do teto.
 * Chave autoIncrement é monotônica → as menores são as mais antigas.
 */
async function enforceRetryableCap(): Promise<void> {
  try {
    const db = await getDB()
    const [pending, failed] = await Promise.all([
      db.countFromIndex('playLogs', 'by-status', 'pending'),
      db.countFromIndex('playLogs', 'by-status', 'failed')
    ])
    const excess = pending + failed - retryableCap
    if (excess <= 0) return

    const [pendingKeys, failedKeys] = await Promise.all([
      db.getAllKeysFromIndex('playLogs', 'by-status', 'pending'),
      db.getAllKeysFromIndex('playLogs', 'by-status', 'failed')
    ])
    const toRemove = [...pendingKeys, ...failedKeys].sort((a, b) => a - b).slice(0, excess)
    const tx = db.transaction('playLogs', 'readwrite')
    await Promise.all([...toRemove.map((id) => tx.store.delete(id)), tx.done])

    droppedCount += toRemove.length
    writeDroppedCount(droppedCount)
    playerLogger.warn('[play-log-manager] Outbox acima do teto -> descartando itens mais antigos', {
      dropped: toRemove.length,
      droppedTotal: droppedCount
    })
  } catch (err) {
    playerLogger.warn('[play-log-manager] Falha ao aplicar teto do outbox', err)
  }
}

function readDroppedCount(): number {
  try {
    const value = Number(localStorage.getItem(DROPPED_COUNTER_KEY) ?? 0)
    return Number.isFinite(value) && value > 0 ? value : 0
  } catch {
    return 0
  }
}

function writeDroppedCount(value: number): void {
  try {
    localStorage.setItem(DROPPED_COUNTER_KEY, String(value))
  } catch {
    // localStorage indisponível: o contador fica só em memória
  }
}
